import React, { useState, useSyncExternalStore } from 'react';
import axios from 'axios';
import {
  X, ChevronDown, ChevronRight, BookOpen, MessageSquare,
  Anchor, GraduationCap, Gamepad2, Copy, Check, Download, Loader2,
  FileText,
} from 'lucide-react';

import { API } from '../App';
import { useAuth } from '../contexts/AuthContext';

/* Module-level cache of lesson_ids that have a home retrieval sheet (404 of
   them — pure game/review days have no sheet by design). Fetched once per app
   load. Exposed via useSyncExternalStore so the component re-renders cleanly
   when the fetch resolves without violating react-hooks/set-state-in-effect. */
let _HOME_SHEET_IDS = null;          // Set<string> | null
let _HOME_SHEET_FETCH_STARTED = false;
const _HOME_SHEET_LISTENERS = new Set();

function _ensureHomeSheetFetch() {
  if (_HOME_SHEET_FETCH_STARTED) return;
  _HOME_SHEET_FETCH_STARTED = true;
  axios.get(`${API}/programs/home-sheets/available`)
    .then((r) => { _HOME_SHEET_IDS = new Set(r.data?.lesson_ids || []); })
    .catch(() => { _HOME_SHEET_IDS = new Set(); })
    .finally(() => { _HOME_SHEET_LISTENERS.forEach((cb) => cb()); });
}

function _subscribeHomeSheetIds(cb) {
  _HOME_SHEET_LISTENERS.add(cb);
  return () => { _HOME_SHEET_LISTENERS.delete(cb); };
}
function _getHomeSheetIds() { return _HOME_SHEET_IDS; }

function useHomeSheetAvailable(lessonId) {
  _ensureHomeSheetFetch();
  const ids = useSyncExternalStore(_subscribeHomeSheetIds, _getHomeSheetIds, _getHomeSheetIds);
  if (!lessonId || !ids) return false;
  return ids.has(lessonId);
}

/* ===================================================================
 * DayScopePanel — the right-side scope pop-up for a single Schedule day.
 *
 * It is ADDITIVE: it does not replace anything. A small "Scope" button on
 * an existing schedule card opens it (see INTEGRATION.md). It shows what a
 * day teaches at a glance:
 *   - the can-do outcome
 *   - Vocabulary (the words taught that day)
 *   - Sentences & phrases (the connected language)
 *   - the cultural anchor + nations
 *   - an expandable curriculum alignment (9 provinces + CEFR)
 *
 * Data comes from src/data/scopeMap/<program_id>.json (the `card` prop is
 * one entry from that file). Reuses the slide-in pattern of the existing
 * Resources viewer so it feels native.
 *
 * Copy / Download (lesson brief PDF):
 *   - Visible only on school audiences (public_school, private_school).
 *   - Demo accounts see the buttons but get an inline nudge — no download.
 *   - Paid school accounts: copy puts plain text on the clipboard; download
 *     fetches a one-page PDF from POST /api/programs/{id}/lesson-card/pdf.
 * =================================================================== */

const SCHOOL_AUDIENCES = new Set(['public_school', 'private_school']);

const CEFR_COLORS = { 'pre-A1': '#2F9E44', 'A1': '#1971C2', 'A1-A2': '#F59F00', 'A2': '#E8590C' };
const cefrColor = (c) => CEFR_COLORS[c] || '#64748B';

function buildClipboardText(card, isFr) {
  const lines = [];
  const meta = [
    card.cefr,
    card.week ? (isFr ? `Semaine ${card.week}` : `Week ${card.week}`) : '',
    card.day,
    card.date,
  ].filter(Boolean).join(' \u00B7 ');
  if (meta) lines.push(meta);
  if (card.title) lines.push(card.title);
  if (card.game) lines.push(`${isFr ? 'Jeu' : 'Game'}: ${card.game}`);
  lines.push('');

  if (card.can_do) {
    lines.push(isFr ? "L'élève peut :" : 'Students can:');
    lines.push(card.can_do);
    lines.push('');
  }
  const recognize = card.recognize || [];
  if (recognize.length) {
    lines.push(`${isFr ? 'Vocabulaire' : 'Vocabulary'} (${recognize.length}):`);
    lines.push(recognize.join(', '));
    lines.push('');
  }
  const produce = card.produce || [];
  if (produce.length) {
    lines.push(`${isFr ? 'Phrases' : 'Sentences & phrases'} (${produce.length}):`);
    produce.forEach((p) => lines.push(`- ${p}`));
    lines.push('');
  }
  const cult = card.culture || {};
  if (cult.anchor) {
    lines.push(isFr ? 'Ancrage culturel :' : 'Cultural anchor:');
    lines.push(cult.anchor);
    if (Array.isArray(cult.nations) && cult.nations.length) {
      lines.push(`(${cult.nations.join(', ')})`);
    }
    lines.push('');
  }
  const cur = card.curriculum || {};
  const rows = cur.rows || [];
  if (cur.cefr || rows.length) {
    lines.push(`${isFr ? 'Alignement au curriculum' : 'Curriculum alignment'}:`);
    if (cur.cefr) lines.push(`  CEFR — ${cur.cefr}`);
    rows.forEach((r) => lines.push(`  ${r.prov} — ${r.codes}`));
  }
  return lines.join('\n').trim();
}

export default function DayScopePanel({
  card,
  accent = '#1971C2',
  isFr = false,
  onClose,
  programId,
  programTitle = '',
  audienceType,
  isDemo = false,
}) {
  const [showCur, setShowCur] = useState(false);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [sheetDownloading, setSheetDownloading] = useState(false);
  const [demoNudge, setDemoNudge] = useState('');
  const { user } = useAuth();
  // Private-center accounts (coaching centers, after-school, camps, etc.) do
  // not get the Download PDF or Home Sheet buttons - they are school-account-only.
  // Schools (public, private, immersion) keep both.
  const isPrivateCenter = (user?.account_type || 'school') === 'private_center';

  // True only when this lesson has a pre-rendered home retrieval sheet on disk.
  // Pure game/review days (46 of 450 lessons) are intentionally without sheets.
  const sheetAvailable = useHomeSheetAvailable(card?.lesson_id);

  if (!card) return null;

  const col = cefrColor(card.cefr);
  const rec = card.recognize || [];        // vocabulary words taught
  const pro = card.produce || [];           // sentences & phrases
  const cult = card.culture || {};
  const cur = card.curriculum || {};
  const rows = cur.rows || [];
  const isCulture = card.item_src === 'focus';
  const metaBits = [
    card.week ? (isFr ? `Semaine ${card.week}` : `Week ${card.week}`) : '',
    card.day, card.date,
  ].filter(Boolean);

  const canShowActions =
    (SCHOOL_AUDIENCES.has(audienceType) && !isPrivateCenter)
    || (isPrivateCenter && sheetAvailable);

  const handleCopy = async () => {
    if (isDemo) {
      setDemoNudge(isFr
        ? "La copie n'est pas disponible sur les comptes de démo. Disponible sur les comptes scolaires complets."
        : 'Copying is not available on demo accounts. Available on full-access school accounts.');
      return;
    }
    try {
      await navigator.clipboard.writeText(buildClipboardText(card, isFr));
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  const handleDownload = async () => {
    // Demo visitors are allowed to download. Feeling the real PDF is the point
    // of the tour. The backend permits invite_lead JWT cookies on both PDF
    // endpoints, so no gating is needed here.
    if (!programId) return;
    setDownloading(true);
    try {
      const resp = await axios.post(
        `${API}/programs/${programId}/lesson-card/pdf`,
        card,
        { responseType: 'blob' },
      );
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = resp.headers && resp.headers['content-disposition'];
      let filename = 'lesson_brief.pdf';
      if (cd) {
        const m = /filename="([^"]+)"/.exec(cd);
        if (m) filename = m[1];
      }
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setDemoNudge(isFr
        ? 'Le téléchargement a échoué. Veuillez réessayer.'
        : 'Download failed. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleHomeSheetDownload = async () => {
    // Demo visitors can download Home Sheets, it's a key part of the tour.
    const lid = card?.lesson_id;
    if (!lid) return;
    setSheetDownloading(true);
    try {
      const resp = await axios.get(
        `${API}/programs/lessons/${encodeURIComponent(lid)}/home-sheet/pdf`,
        { responseType: 'blob' },
      );
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = resp.headers && resp.headers['content-disposition'];
      let filename = `FrenchBoard_home_sheet_${lid}.pdf`;
      if (cd) {
        const m = /filename="([^"]+)"/.exec(cd);
        if (m) filename = m[1];
      }
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setDemoNudge(isFr
        ? "Le téléchargement de la fiche-maison a échoué. Veuillez réessayer."
        : 'Home sheet download failed. Please try again.');
    } finally {
      setSheetDownloading(false);
    }
  };

  return (
    <div
      data-testid="day-scope-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'flex-end' }}
    >
      <div data-testid="day-scope-panel" style={{
        width: 'min(560px, 92vw)', height: '100%', background: 'white',
        overflow: 'auto', boxShadow: '-8px 0 30px rgba(0,0,0,0.15)', fontFamily: 'Nunito, sans-serif',
      }}>
        {/* Header */}
        <div style={{
          padding: '1.1rem 1.5rem', borderBottom: '1px solid #E2E8F0',
          position: 'sticky', top: 0, background: 'white', zIndex: 2,
          display: 'flex', alignItems: 'flex-start', gap: 12,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 5, flexWrap: 'wrap' }}>
              <CefrPill cefr={card.cefr} col={col} />
              {metaBits.map((t, i) => <span key={i} style={{ fontSize: '0.74rem', color: '#94A3B8' }}>{t}</span>)}
            </div>
            <h2 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.15rem', fontWeight: 700, color: '#1A1A2E', margin: 0, lineHeight: 1.25 }}>
              {card.title}
            </h2>
            {card.game && (
              <div style={{ fontSize: '0.76rem', color: '#64748B', marginTop: 4, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <Gamepad2 size={12} /> {isFr ? 'Jeu' : 'Game'}: {card.game}
              </div>
            )}
          </div>
          <button onClick={onClose} data-testid="day-scope-close" style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 34, height: 34, borderRadius: 8, border: '1px solid #E2E8F0',
            background: 'white', cursor: 'pointer', color: '#64748B', flexShrink: 0,
          }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: '1.25rem 1.5rem' }}>
          {/* Can-do */}
          {card.can_do && (
            <div style={{ background: accent + '0A', borderLeft: `3px solid ${accent}`, borderRadius: '0 10px 10px 0', padding: '0.7rem 0.9rem', marginBottom: '1.25rem' }}>
              <div style={{ fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: accent, marginBottom: 3 }}>
                {isFr ? 'L’élève peut' : 'Students can'}
              </div>
              <div style={{ fontSize: '0.92rem', color: '#1A1A2E', lineHeight: 1.55 }}>{card.can_do}</div>
            </div>
          )}

          {/* Vocabulary / Sentences */}
          {(rec.length > 0 || pro.length > 0) ? (
            <div style={{ marginBottom: '1.25rem' }}>
              {rec.length > 0 && <ItemList title={isFr ? 'Vocabulaire' : 'Vocabulary'} icon={BookOpen} color="#0E7490" items={rec} />}
              {pro.length > 0 && <div style={{ marginTop: rec.length ? '1rem' : 0 }}>
                <ItemList title={isFr ? 'Phrases' : 'Sentences & phrases'} icon={MessageSquare} color="#B45309" items={pro} />
              </div>}
            </div>
          ) : isCulture ? (
            <div style={{ fontSize: '0.85rem', color: '#64748B', marginBottom: '1.25rem', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <Anchor size={15} color="#7C3AED" style={{ marginTop: 2, flexShrink: 0 }} />
              <span>{isFr ? 'Journée culturelle et de lecture, axée sur l’ancrage ci-dessous.' : 'A cultural and reading day, focused on the anchor below.'}</span>
            </div>
          ) : null}

          {/* Cultural anchor */}
          {cult.anchor && (
            <div style={{ marginBottom: '1.25rem' }}>
              <SectionLabel icon={Anchor} color="#7C3AED" text={isFr ? 'Ancrage culturel' : 'Cultural anchor'} />
              <p style={{ fontSize: '0.88rem', color: '#334155', lineHeight: 1.6, margin: '0.4rem 0 0' }}>{cult.anchor}</p>
              {Array.isArray(cult.nations) && cult.nations.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                  {cult.nations.map((n, i) => (
                    <span key={i} style={{ fontSize: '0.7rem', fontWeight: 700, color: '#7C3AED', background: '#7C3AED14', borderRadius: 5, padding: '2px 7px' }}>{n}</span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Curriculum alignment (expandable) */}
          {(rows.length > 0 || cur.cefr) && (
            <div style={{ border: '1px solid #E2E8F0', borderRadius: 10, overflow: 'hidden' }}>
              <button onClick={() => setShowCur((s) => !s)} data-testid="day-scope-curriculum-toggle" style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                padding: '0.7rem 0.9rem', background: showCur ? '#F8FAFC' : 'white',
                border: 'none', cursor: 'pointer', fontFamily: 'Nunito, sans-serif',
              }}>
                <GraduationCap size={15} color="#475569" />
                <span style={{ flex: 1, textAlign: 'left', fontFamily: 'Outfit, sans-serif', fontSize: '0.85rem', fontWeight: 600, color: '#475569' }}>
                  {isFr ? 'Alignement au curriculum' : 'Curriculum alignment'}
                  <span style={{ color: '#94A3B8', fontWeight: 400 }}>
                    {rows.length > 0
                      ? ` · ${rows.length} provinces${cur.cefr ? ' + CEFR' : ''}`
                      : (cur.cefr ? (isFr ? ' · niveau CECR' : ' · CEFR level') : '')}
                  </span>
                </span>
                {showCur ? <ChevronDown size={16} color="#94A3B8" /> : <ChevronRight size={16} color="#94A3B8" />}
              </button>
              {showCur && (
                <div style={{ borderTop: '1px solid #E2E8F0' }}>
                  {cur.cefr && <CurRow prov="CEFR" codes={cur.cefr} highlight />}
                  {rows.map((r, i) => <CurRow key={i} prov={r.prov} codes={r.codes} />)}
                </div>
              )}
            </div>
          )}

          {/* Copy / Download — school programs only. Demo accounts see the
              buttons but are nudged toward a full account on click. */}
          {canShowActions && (
            <div data-testid="day-scope-actions" style={{ marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px solid #F1F5F9' }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={handleCopy}
                  data-testid="day-scope-copy-btn"
                  style={{
                    display: isPrivateCenter ? 'none' : 'inline-flex', alignItems: 'center', gap: 6,
                    fontFamily: 'Outfit, sans-serif', fontSize: '0.82rem', fontWeight: 600,
                    color: copied ? '#15803D' : accent,
                    background: copied ? '#DCFCE7' : accent + '0F',
                    border: `1px solid ${copied ? '#86EFAC' : accent + '33'}`,
                    borderRadius: 8, padding: '0.5rem 0.85rem', cursor: 'pointer',
                    transition: 'background 0.15s',
                  }}
                >
                  {copied
                    ? <><Check size={14} /> {isFr ? 'Copié' : 'Copied'}</>
                    : <><Copy size={14} /> {isFr ? 'Copier' : 'Copy'}</>}
                </button>
                <button
                  type="button"
                  onClick={handleDownload}
                  disabled={downloading}
                  data-testid="day-scope-download-btn"
                  style={{
                    display: isPrivateCenter ? 'none' : 'inline-flex', alignItems: 'center', gap: 6,
                    fontFamily: 'Outfit, sans-serif', fontSize: '0.82rem', fontWeight: 600,
                    color: 'white', background: accent,
                    border: `1px solid ${accent}`,
                    borderRadius: 8, padding: '0.5rem 0.85rem',
                    cursor: downloading ? 'wait' : 'pointer',
                    opacity: downloading ? 0.75 : 1, transition: 'opacity 0.15s',
                  }}
                >
                  {downloading
                    ? <><Loader2 size={14} className="spin" /> {isFr ? 'Préparation…' : 'Preparing…'}</>
                    : <><Download size={14} /> {isFr ? 'Télécharger le PDF' : 'Download PDF'}</>}
                </button>
                {sheetAvailable && (
                  <button
                    type="button"
                    onClick={handleHomeSheetDownload}
                    disabled={sheetDownloading}
                    data-testid="day-scope-home-sheet-btn"
                    title={isFr
                      ? 'Fiche A4 à rapporter à la maison pour le rappel autonome'
                      : 'A4 take-home sheet for at-home recall practice'}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      fontFamily: 'Outfit, sans-serif', fontSize: '0.82rem', fontWeight: 600,
                      color: accent,
                      background: 'white',
                      border: `1px solid ${accent}66`,
                      borderRadius: 8, padding: '0.5rem 0.85rem',
                      cursor: sheetDownloading ? 'wait' : 'pointer',
                      opacity: sheetDownloading ? 0.75 : 1, transition: 'opacity 0.15s',
                    }}
                  >
                    {sheetDownloading
                      ? <><Loader2 size={14} className="spin" /> {isFr ? 'Préparation…' : 'Preparing…'}</>
                      : <><FileText size={14} /> {isFr ? 'Fiche maison' : 'Home sheet'}</>}
                  </button>
                )}
              </div>
              {isDemo && (
                <div data-testid="day-scope-demo-hint" style={{
                  marginTop: 8, fontSize: '0.74rem', color: '#94A3B8', fontStyle: 'italic',
                }}>
                  {isFr
                    ? "Aperçu de démo. Essayez le téléchargement, c'est inclus dans la visite."
                    : "Demo preview. Go ahead and try the downloads, they're part of the tour."}
                </div>
              )}
              {demoNudge && (
                <div data-testid="day-scope-demo-nudge" style={{
                  marginTop: 8, fontSize: '0.78rem', color: '#92400E',
                  background: '#FEF3C7', border: '1px solid #FCD34D',
                  borderRadius: 8, padding: '0.5rem 0.7rem', lineHeight: 1.45,
                }}>
                  {demoNudge}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CefrPill({ cefr, col }) {
  if (!cefr) return null;
  return <span style={{ fontSize: '0.68rem', fontWeight: 700, color: 'white', background: col, borderRadius: 5, padding: '2px 7px' }}>{cefr}</span>;
}

function ItemList({ title, icon: Icon, color, items }) {
  return (
    <div>
      <SectionLabel icon={Icon} color={color} text={`${title} · ${items.length}`} />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
        {items.map((it, i) => (
          <span key={i} style={{
            fontSize: '0.82rem', color: '#1A1A2E', background: color + '10',
            border: `1px solid ${color}26`, borderRadius: 7, padding: '4px 9px', lineHeight: 1.3,
          }}>{it}</span>
        ))}
      </div>
    </div>
  );
}

function SectionLabel({ icon: Icon, color, text }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <Icon size={14} color={color} />
      <span style={{ fontFamily: 'Outfit, sans-serif', fontSize: '0.74rem', fontWeight: 700, letterSpacing: '0.03em', textTransform: 'uppercase', color }}>{text}</span>
    </div>
  );
}

function CurRow({ prov, codes, highlight }) {
  return (
    <div style={{ display: 'flex', gap: 10, padding: '0.55rem 0.9rem', borderBottom: '1px solid #F1F5F9', background: highlight ? '#F8FAFC' : 'white' }}>
      <div style={{ width: 120, flexShrink: 0, fontSize: '0.78rem', fontWeight: 700, color: highlight ? '#1971C2' : '#475569', fontFamily: 'Outfit, sans-serif' }}>{prov}</div>
      <div style={{ flex: 1, fontSize: '0.8rem', color: '#334155', lineHeight: 1.45 }}>{codes}</div>
    </div>
  );
}
